import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { isSupabaseConfigured, supabase } from '../lib/supabase'

type AuthContextValue = {
  user: User | null
  session: Session | null
  loading: boolean
  demo: boolean
  signIn: (email: string, password: string) => Promise<string | null>
  signUp: (name: string, email: string, password: string, interests?: string[]) => Promise<string | null>
  requestPasswordReset: (email: string) => Promise<string | null>
  updatePassword: (password: string) => Promise<string | null>
  signOut: () => Promise<void>
  enterDemo: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

function friendlyAuthError(message?: string) {
  const text = (message || '').toLowerCase()
  if (!text) return null
  if (text.includes('invalid login credentials')) return 'E-mail ou senha não conferem.'
  if (text.includes('email not confirmed')) return 'Confirme seu e-mail antes de entrar.'
  if (text.includes('user already registered')) return 'Já existe uma conta com este e-mail.'
  if (text.includes('password')) return 'A senha não atende aos requisitos de segurança.'
  if (text.includes('rate limit')) return 'Muitas tentativas em pouco tempo. Aguarde alguns minutos e tente novamente.'
  return 'Não foi possível concluir agora. Confira os dados e tente novamente.'
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(isSupabaseConfigured)
  const [demo, setDemo] = useState(() => localStorage.getItem('livroom-demo') === '1')

  useEffect(() => {
    if (!supabase) { setLoading(false); return }
    let mounted = true
    supabase.auth.getSession().then(({ data }) => { if (mounted) { setSession(data.session); setLoading(false) } })
    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession)
      if (nextSession) { localStorage.removeItem('livroom-demo'); setDemo(false) }
      setLoading(false)
    })
    return () => { mounted = false; listener.subscription.unsubscribe() }
  }, [])

  const value = useMemo<AuthContextValue>(() => ({
    user: session?.user ?? null,
    session,
    loading,
    demo,
    async signIn(email, password) {
      if (!supabase) return 'O Supabase ainda não foi configurado. Use o modo demonstração.'
      const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password })
      if (!error) { localStorage.removeItem('livroom-demo'); setDemo(false) }
      return friendlyAuthError(error?.message)
    },
    async signUp(name, email, password, interests = []) {
      if (!supabase) return 'O Supabase ainda não foi configurado. Use o modo demonstração.'
      const { error } = await supabase.auth.signUp({
        email: email.trim(), password,
        options: { data: { name: name.trim(), interests }, emailRedirectTo: `${window.location.origin}/login?confirmed=1` }
      })
      return friendlyAuthError(error?.message)
    },
    async requestPasswordReset(email) {
      if (!supabase) return 'O Supabase ainda não foi configurado.'
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: `${window.location.origin}/login?reset=1` })
      return friendlyAuthError(error?.message)
    },
    async updatePassword(password) {
      if (!supabase) return 'O Supabase ainda não foi configurado.'
      const { error } = await supabase.auth.updateUser({ password })
      return friendlyAuthError(error?.message)
    },
    async signOut() {
      localStorage.removeItem('livroom-demo')
      setDemo(false)
      if (supabase) await supabase.auth.signOut()
    },
    enterDemo() { localStorage.setItem('livroom-demo','1'); setDemo(true) }
  }), [demo, loading, session])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used inside AuthProvider')
  return value
}
